import React, { MouseEventHandler, useContext, useMemo } from 'react'
import FileSaver from 'file-saver'
import { Empty } from 'google-protobuf/google/protobuf/empty_pb'
import { ModelPromiseClient } from '@trustero/trustero-api-web/lib/model/model_grpc_web_pb'
import {
  Control,
  ListControlsRequest,
} from '@trustero/trustero-api-web/lib/model/control_pb'
import { BoolValue } from 'google-protobuf/google/protobuf/wrappers_pb'
import { getActiveServices } from 'src/pages/Services/services.helpers'
import { useContent } from 'src/context/hooks'
import {
  GetLatestTestResultRequest,
  TestResult,
} from '@trustero/trustero-api-web/lib/evidence/testing_pb'
import { Identifier } from '@trustero/trustero-api-web/lib/common/model_pb'
import { MODEL_TYPE } from '@trustero/trustero-api-web/lib/common/model_pb'
import {
  ListServicesRequest,
  ServiceRecord,
} from '@trustero/trustero-api-web/lib/service/service_pb'
import { useServices } from 'src/pages/Services/Services.hooks'
import { ConfirmationContext } from '../../../../../Confirmation'
import { SOC2Report } from '../../../../../Utils/reports/SOC2Report'
import { Soc2ReportModal } from '../Soc2ReportModal'
import { useSwrImmutableGrpc } from '../../../../../components/async/useSwrImmutableGrpc'
import { Spinner } from '../../../../../Throbber'
import { useAccount } from '../../../../../components/async/Account'
import { useLatestTestResults } from '../../../../../components/async/suggestions'

export const useDownloadSoc2Report =
  (): MouseEventHandler<HTMLButtonElement> => {
    const content = useContent()
    const { setConfirmationState } = useContext(ConfirmationContext)
    const req = new ListServicesRequest()
    const { data: serviceRes } = useServices(req)
    const {
      response: { data: controls },
    } = useSwrImmutableGrpc(
      ModelPromiseClient.prototype.listControls,
      new ListControlsRequest().setIsDismissed(new BoolValue().setValue(false)),
    )
    const {
      response: { data: frameworks },
    } = useSwrImmutableGrpc(
      ModelPromiseClient.prototype.listFrameworks,
      new Empty(),
    )
    const { data: accountRes } = useAccount()
    const account = accountRes?.toObject()
    const controlIdentifiers =
      controls
        ?.getItemsList()
        .map((c) =>
          new Identifier()
            .setModeltype(MODEL_TYPE.CONTROL)
            .setModelid(c.getModelId()),
        ) ?? []
    const { data: testResultsResponse } = useLatestTestResults(
      new GetLatestTestResultRequest().setModelIdsList(controlIdentifiers),
      controlIdentifiers.length > 0,
    )
    const services = serviceRes
      ? serviceRes.getServicesList()
      : ([] as ServiceRecord[])
    const activeServices = getActiveServices(services, '')

    const entity = account?.orgname !== '' ? account?.orgname : 'Entity'

    const data: { control: Control.AsObject; tests: TestResult[] }[] =
      useMemo(() => {
        const latestResultsPerControl =
          testResultsResponse
            ?.getItemsList()
            .filter((result) => result.hasTestResult())
            .map((result) => result.getTestResult() as TestResult)
            .filter((test) => {
              // Filter out Freshness Tests
              return test.getTestdefinitionid() !== 'trt0'
            })
            .reduce((acc, curr) => {
              if (!acc[curr.getControlId()]) {
                acc[curr.getControlId()] = []
              }
              acc[curr.getControlId()].push(curr)
              return acc
            }, {} as { [key: string]: TestResult[] }) ?? {}

        return (
          controls?.toObject().itemsList.map((control) => ({
            control,
            tests: latestResultsPerControl[control.id] ?? [],
          })) ?? []
        )
      }, [controls, testResultsResponse])

    const body = data.length ? (
      <Soc2ReportModal />
    ) : (
      <Spinner color="primary" size="xxl" mt="xl" />
    )

    return () => {
      setConfirmationState({
        isShown: true,
        size: 'lg',
        title: 'Export PDF Pro Forma SOC 2 Report',
        body,
        confirmText: 'Export PDF',
        onConfirmCB: async () => {
          if (!frameworks || !entity || !data) {
            return
          }
          const report = new SOC2Report()
          const pdf = await report.report(
            entity,
            content,
            activeServices,
            frameworks.toObject().itemsList,
            data,
          )
          return FileSaver.saveAs(pdf, 'Pro-Forma-SOC2.pdf')
        },
      })
    }
  }
